import { MenuButton } from '@szhsin/react-menu';
import styled, { css } from 'styled-components';

export const ProfileShell = styled.div`
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  /* Center page-level loaders within the content column */
  > div:only-child {
    flex: 1;
    min-height: 0 !important;
  }
`;

export const StyledAgentProfile = styled.div`
  width: 100%;
  color: var(--ink);

  .head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 32px;

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: flex-start;
    }

    .img-box {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      overflow: hidden;
      width: 64px;
      height: 64px;
      border-radius: 16px;
      background: var(--o-tint);
      border: 1px solid var(--line);

      img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
      }

      .fallback-icon {
        color: var(--o2);
      }
    }

    .head-details {
      flex: 1;
      min-width: 0;
    }

    .name {
      margin: 0 0 8px;
      font-size: 28px;
      line-height: 34px;
      font-weight: 700;
      color: var(--ink);
      text-transform: capitalize;

      @media (min-width: 768px) {
        font-size: 30px;
        line-height: 36px;
      }
    }

    .description {
      margin: 0 0 12px;
      max-width: 42rem;
      font-size: 15px;
      line-height: 22px;
      color: var(--quiet);
    }

    .meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 16px;
      font-size: 12px;
      line-height: 16px;
      color: var(--quiet);
    }

    .meta-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .meta-id {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      word-break: break-all;
    }

    .edit-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      padding: 8px 14px;
      border-radius: 9999px;
      border: 1px solid var(--line);
      background: var(--card);
      color: var(--ink);
      font-size: 14px;
      line-height: 18px;
      cursor: pointer;
      box-shadow: var(--shadow);
      transition:
        border-color 0.2s ease,
        background 0.2s ease;

      &:hover {
        border-color: var(--o1);
        background: var(--o-tint);
      }
    }
  }

  .row {
    display: grid;
    gap: 24px;

    @media (min-width: 992px) {
      grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
      align-items: start;
    }

    .col,
    .col-2 {
      width: 100%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
  }
`;

export const CardHolder = styled.div`
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
  background: var(--card);
  box-shadow: var(--shadow);
  color: var(--ink);

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--line);

    .card-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: var(--ink);

      svg {
        color: var(--o2);
        flex-shrink: 0;
      }
    }

    .total-memories {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid var(--line);
      color: var(--quiet);
      background: var(--card-warm);
    }
  }

  .card-details {
    padding: 20px;
    color: var(--fg-2);
  }

  .instruction {
    margin: 0;
    font-size: 14px;
    line-height: 22px;
    color: var(--fg-2);

    h2,
    h3,
    h4 {
      margin: 0 0 10px;
      color: var(--ink);
      font-weight: 700;
    }

    h2 {
      font-size: 18px;
      line-height: 24px;
    }

    h3 {
      font-size: 16px;
      line-height: 22px;
    }

    h4 {
      font-size: 14px;
      line-height: 20px;
    }

    p {
      margin: 0 0 10px;
    }

    ul,
    ol {
      margin: 0 0 12px;
      padding-left: 1.25rem;
    }

    strong {
      color: var(--ink);
      font-weight: 600;
    }
  }

  .memory-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
  }

  .memory {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    background: var(--card-warm);
    border-radius: 12px;
    border: 1px solid var(--line);

    .memory-body {
      min-width: 0;
      flex: 1;
    }

    .memory-heading {
      margin: 0;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: var(--ink);
      word-break: break-word;
    }

    .memory-date {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      line-height: 16px;
      color: var(--quiet);
    }

    .category-wrap {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      flex-shrink: 0;
    }

    .category {
      display: inline-block;
      text-transform: capitalize;
      font-size: 12px;
      line-height: 16px;
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid var(--line);
      color: var(--quiet);
      background: var(--card);

      &.accent {
        color: var(--o-deep);
        background: var(--o-tint);
        border-color: var(--line);
      }
    }
  }

  .tools-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .tool-chip {
    display: inline-block;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    text-transform: capitalize;
    padding: 6px 10px;
    border-radius: 999px;
    color: #0f766e;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  ${({ $config }) =>
    $config &&
    css`
      .card-details {
        padding: 8px 20px 12px;
      }

      .list-wrap {
        li {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid var(--line);

          .li-title {
            margin: 0;
            font-size: 14px;
            line-height: 20px;
            color: var(--quiet);
          }

          .li-value {
            margin: 0;
            font-size: 14px;
            line-height: 20px;
            font-weight: 600;
            color: var(--ink);
            text-align: right;
            text-transform: none;

            &.model {
              word-break: break-all;
            }

            &.badge {
              font-size: 12px;
              line-height: 16px;
              border-radius: 999px;
              padding: 4px 8px;
              border: 1px solid transparent;
            }

            &.enabled {
              background: var(--o-tint);
              color: var(--o-deep);
              border-color: var(--line);
            }

            &.disabled {
              background: var(--card-warm);
              color: var(--quiet);
              border-color: var(--line);
            }
          }

          &:last-child {
            border-bottom: 0;
          }
        }
      }
    `}
`;

export const MenuBtn = styled(MenuButton)`
  position: relative;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
